import { GameIcon, StatIcon } from "../assets/gameIcons.js";
import { useEffect, useState } from "react";
import { LEADERBOARDS } from "@zend/content";
import { pick, useT } from "../i18n/index.js";
import { formatCompact } from "../ui/Counter.js";
import { useSession } from "../state/session.js";
import { boardsApi } from "./api.js";
import { useSocial } from "./state.js";
import type { BoardRow } from "./types.js";

/** Leaderboards: one board per tab, top 100, own rank pinned, weekly snapshots (R75). */
export function BoardsView() {
  const t = useT();
  const board = useSocial((s) => s.board);
  const boardId = useSocial((s) => s.boardId);
  const refresh = useSocial((s) => s.refreshBoard);
  const openProfile = useSocial((s) => s.openProfile);
  const account = useSession((s) => s.account);
  const [week, setWeek] = useState<string>("live");
  const [history, setHistory] = useState<BoardRow[] | null>(null);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  useEffect(() => {
    if (week === "live" || !board) {
      setHistory(null);
      return;
    }
    void boardsApi.history(board.board, week).then(setHistory, () => setHistory(null));
  }, [week, board]);
  const def = LEADERBOARDS.find((b) => b.id === boardId);
  const rows = history ?? board?.rows ?? [];
  const meInTop = board?.me && rows.some((r) => r.accountId === board.me!.accountId);
  return (
    <div data-testid="boards-view">
      <div className="row" style={{ gap: 6, flexWrap: "wrap", padding: "0 12px 8px" }}>
        {LEADERBOARDS.map((b) => (
          <button key={b.id} type="button" className={`tab ${b.id === boardId ? "active" : ""}`} onClick={() => { setWeek("live"); void refresh(b.id); }} data-testid={`board-${b.id}`}>
            {pick(b.name)}
          </button>
        ))}
        <span className="grow" />
        {board && board.snapshots.length ? (
          <select className="field" value={week} onChange={(e) => setWeek(e.target.value)} data-testid="board-week">
            <option value="live">{t("social.boards.live")}</option>
            {board.snapshots.map((w) => (
              <option key={w} value={w}>
                {w}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      {!board ? <p className="dim" style={{ padding: 12 }}>{t("common.loading")}</p> : null}
      {board?.me && !meInTop && week === "live" ? (
        <div className="row list-row board-row me" data-testid="board-me">
          <span className="mono" style={{ width: 44 }}>#{board.me.rank}</span>
          <span className="grow" style={{ fontWeight: 800 }}>{board.me.name}</span>
          <span className="mono">{formatCompact(board.me.value)} {def?.id === "power" ? <StatIcon stat="power" label={pick(def.unit)} /> : def ? pick(def.unit) : ""}</span>
        </div>
      ) : null}
      {rows.map((r) => (
        <div key={r.accountId} className={`row list-row board-row ${r.accountId === account?.id ? "me" : ""}`} data-testid={`board-row-${r.rank}`}>
          <span className="mono" style={{ width: 44, color: r.rank <= 3 ? "var(--gold)" : undefined }}>
            {r.rank <= 3 ? <GameIcon id={["ui.medal.gold", "ui.medal.silver", "ui.medal.bronze"][r.rank - 1]!} size={28} label={`#${r.rank}`} /> : `#${r.rank}`}
          </span>
          <button type="button" className="plain grow" style={{ textAlign: "left", fontWeight: r.accountId === account?.id ? 800 : 600 }} onClick={() => void openProfile(r.accountId)}>
            {r.name} {r.bot ? <span className="hint">NPC</span> : null}
          </button>
          <span className="mono">{formatCompact(r.value)} {def?.id === "power" ? <StatIcon stat="power" label={pick(def.unit)} /> : def ? pick(def.unit) : ""}</span>
        </div>
      ))}
      {board && rows.length === 0 ? <p className="dim" style={{ padding: 12 }}>{t("social.boards.empty")}</p> : null}
    </div>
  );
}
